import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { enablePush } from '../features/push/pushTokens';
import { triggerHaptic } from '../features/ui/haptics';

const PROMPT_DISMISSED_KEY = 'talk2nick_push_prompt_dismissed';

export function PushPermissionPrompt({ userId }: { userId: string | null }) {
  const { t } = useTranslation();

  const [visible, setVisible] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof Notification === 'undefined') return false;
    if (Notification.permission !== 'default') return false;
    try {
      const dismissed = sessionStorage.getItem(PROMPT_DISMISSED_KEY);
      return dismissed !== '1';
    } catch {
      return true;
    }
  });

  const [isBusy, setIsBusy] = useState(false);
  const [iosAlert, setIosAlert] = useState<string | null>(null);

  const onRequestPermission = useCallback(async () => {
    if (typeof window === 'undefined' || typeof Notification === 'undefined' || !userId || isBusy)
      return;

    triggerHaptic('medium');

    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      Boolean((navigator as unknown as { standalone?: boolean }).standalone);

    if (isIOS && !isStandalone) {
      setIosAlert(t('push.promptIosHint'));
      return;
    }

    setIsBusy(true);
    try {
      // Must execute directly from the click event to satisfy iOS WebKit gesture requirement
      const reg = await enablePush(userId);
      if (reg.state === 'active') {
        triggerHaptic('success');
      }
    } catch (e) {
      console.warn('[talk2nIcK] Push permission request failed:', e);
    } finally {
      setIsBusy(false);
      setVisible(false);
    }
  }, [userId, isBusy, t]);

  const onDismiss = useCallback(() => {
    triggerHaptic('light');
    try {
      sessionStorage.setItem(PROMPT_DISMISSED_KEY, '1');
    } catch {
      // ignore storage access errors (private mode, etc.)
    }
    setVisible(false);
  }, []);

  if (!visible || !userId) return null;

  return (
    <div
      className="inapp-banner pointer-events-auto w-full max-w-[480px]"
      role="alertdialog"
      aria-labelledby="push-prompt-title"
    >
      <div className="relative overflow-hidden rounded-2xl border border-[var(--divider)] bg-[var(--bg-surface-elevated)]/95 p-4 shadow-2xl backdrop-blur-xl">
        <div className="flex items-start gap-3.5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--accent)]/15 text-xl">
            🔔
          </div>
          <div className="min-w-0 flex-1">
            <h3 id="push-prompt-title" className="text-sm font-semibold text-[var(--text-primary)]">
              {t('push.promptTitle')}
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-[var(--text-secondary)]">
              {t('push.promptDesc')}
            </p>
            {iosAlert && (
              <p className="mt-2 rounded-lg bg-[var(--accent)]/10 p-2 text-xs font-medium text-[var(--accent)]">
                {iosAlert}
              </p>
            )}
            <div className="mt-3.5 flex items-center gap-2">
              <button
                type="button"
                onClick={onRequestPermission}
                disabled={isBusy}
                className="pressable rounded-lg bg-[var(--accent)] px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm disabled:opacity-50"
              >
                {isBusy ? t('push.stateRegistering') : t('push.promptEnable')}
              </button>
              <button
                type="button"
                onClick={onDismiss}
                className="pressable rounded-lg border border-[var(--divider)] bg-[var(--bg-app)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                {t('push.promptLater')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
